import { useState } from "react";
import { api, ApiError } from "@/lib/apiClient";
import { formatDate } from "@/lib/date";
import { StatusBadge } from "./StatusBadge";
import { ReturnModal } from "./ReturnModal";
import { ConfirmDialog } from "./ConfirmDialog";
import type { Reservation } from "@/types/reservation";
import type { CurrentUser } from "@/types/user";
import { CANCELLABLE_STATUSES, RETURNABLE_STATUSES } from "@/lib/status";
import { useToast } from "@/hooks/useToast";

export function ReservationModal({
  reservation,
  pcName,
  userName,
  currentUser,
  onClose,
  onChanged,
}: {
  reservation: Reservation;
  pcName: string;
  userName: string;
  currentUser: CurrentUser;
  onClose: () => void;
  onChanged: (updated: Reservation) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const notify = useToast();

  const isManager = currentUser.role === "manager" || currentUser.role === "admin";
  const isOwner = reservation.user_id === currentUser.id || reservation.requested_by_user_id === currentUser.id;

  // 自分自身が申請した予約（自分名義 or 自分が代理申請したもの）は、
  // manager/adminであっても承認・却下できない（本人承認の禁止）。
  const canApproveReject = isManager && !isOwner && reservation.status === "pending";
  const canCancel = (isOwner || isManager) && CANCELLABLE_STATUSES.includes(reservation.status);
  const canReturn =
    (reservation.user_id === currentUser.id || isManager) && RETURNABLE_STATUSES.includes(reservation.status);

  const runAction = async (action: () => Promise<Reservation>, successMessage: string) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await action();
      notify(successMessage);
      onChanged(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "処理に失敗しました。");
    } finally {
      setBusy(false);
    }
  };

  const approve = () =>
    runAction(() => api.post<Reservation>(`/reservations/${reservation.id}/approve`), "承認しました。");
  const reject = () =>
    runAction(async () => {
      const result = await api.post<Reservation>(`/reservations/${reservation.id}/reject`, {});
      setShowRejectConfirm(false);
      return result;
    }, "却下しました。");
  const cancel = () =>
    runAction(async () => {
      const result = await api.post<Reservation>(`/reservations/${reservation.id}/cancel`);
      setShowCancelConfirm(false);
      return result;
    }, "予約をキャンセルしました。");

  if (showReturnModal) {
    return (
      <ReturnModal
        reservation={reservation}
        pcName={pcName}
        onClose={() => setShowReturnModal(false)}
        onReturned={(updated) => {
          setShowReturnModal(false);
          notify("返却処理を記録しました。");
          onChanged(updated);
        }}
      />
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title" style={{ margin: 0 }}>
            予約詳細
          </h2>
          <button className="modal-close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          <div>
            <StatusBadge status={reservation.status} />
          </div>
          <DetailRow label="使用者" value={userName} />
          <DetailRow label="使用PC" value={pcName} />
          <DetailRow
            label="利用期間"
            value={`${formatDate(reservation.start_at)}〜${formatDate(reservation.end_at)}`}
          />
          {reservation.purpose && <DetailRow label="利用目的" value={reservation.purpose} />}
          {reservation.notes && <DetailRow label="備考" value={reservation.notes} />}
          {reservation.status === "rejected" && reservation.rejection_reason && (
            <DetailRow label="却下理由" value={reservation.rejection_reason} />
          )}
          {reservation.return_condition && (
            <DetailRow
              label="返却状態"
              value={
                reservation.return_condition === "no_problem"
                  ? "問題なし"
                  : reservation.return_condition === "problem"
                    ? "不具合あり"
                    : "備品不足あり"
              }
            />
          )}
        </div>

        {error && <p className="error-text">{error}</p>}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {canApproveReject && (
            <div className="form-row">
              <button className="btn btn-secondary btn-block" onClick={() => setShowRejectConfirm(true)} disabled={busy}>
                却下する
              </button>
              <button className="btn btn-primary btn-block" onClick={approve} disabled={busy}>
                承認する
              </button>
            </div>
          )}
          {canReturn && (
            <button className="btn btn-primary btn-block" onClick={() => setShowReturnModal(true)} disabled={busy}>
              PCを返却しました
            </button>
          )}
          {canCancel && (
            <button className="btn btn-danger btn-block" onClick={() => setShowCancelConfirm(true)} disabled={busy}>
              キャンセルする
            </button>
          )}
        </div>
      </div>

      {showCancelConfirm && (
        <ConfirmDialog
          title="予約をキャンセルしますか？"
          message="この操作は取り消せません。"
          confirmLabel="キャンセルする"
          danger
          busy={busy}
          onConfirm={cancel}
          onCancel={() => setShowCancelConfirm(false)}
        />
      )}
      {showRejectConfirm && (
        <ConfirmDialog
          title="この申請を却下しますか？"
          message="申請者へLINEで通知されます。"
          confirmLabel="却下する"
          danger
          busy={busy}
          onConfirm={reject}
          onCancel={() => setShowRejectConfirm(false)}
        />
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", gap: 12, fontSize: 14 }}>
      <span style={{ width: 88, flexShrink: 0, color: "var(--color-text-muted)" }}>{label}</span>
      <span style={{ flex: 1 }}>{value}</span>
    </div>
  );
}
